import {
  CheckCircle,
  Close,
  Error,
  PowerOff,
  QrCode,
  Refresh,
  Schedule,
  WhatsApp
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CustomDialog from 'components/shared/dialog/CustomDialog';
import { useState } from 'react';

/**
 * Card para exibir informações de uma conexão WhatsApp
 * Seguindo padrões de design do projeto JBE Engenharia
 */
const WhatsAppConnectionCard = ({
  connection,
  onConnect,
  onDisconnect,
  onRestart,
  loading = false,
  loadingAction = null, // 'connect' | 'disconnect' | 'restart'
}) => {
  const theme = useTheme();
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  // Status mapping para cores e ícones
  const statusConfig = {
    close: {
      color: 'default',
      bgColor: theme.palette.grey[100],
      textColor: theme.palette.text.secondary,
      icon: <Close sx={{ color: theme.palette.grey[500] }} />,
      label: 'Desconectado',
    },
    connecting: {
      color: 'warning',
      bgColor: theme.palette.warning.light,
      textColor: theme.palette.warning.contrastText,
      icon: <Schedule sx={{ color: theme.palette.warning.main }} />,
      label: 'Conectando',
    },
    open: {
      color: 'success',
      bgColor: theme.palette.success.light,
      textColor: theme.palette.success.contrastText,
      icon: <CheckCircle sx={{ color: theme.palette.success.main }} />,
      label: 'Conectado',
    },
    failed: {
      color: 'error',
      bgColor: theme.palette.error.light,
      textColor: theme.palette.error.contrastText,
      icon: <Error sx={{ color: theme.palette.error.main }} />,
      label: 'Falha na conexão',
    },
  };

  const currentStatus = statusConfig[connection.status] || statusConfig.close;

  const handleConnect = () => {
    onConnect(connection.id);
  };

  const handleDisconnect = () => {
    onDisconnect(connection.id);
    setShowDisconnectDialog(false);
  };

  const handleRestart = () => {
    onRestart(connection.id);
  };

  const formatLastUpdate = (updatedAt) => {
    if (!updatedAt) return 'Nunca';
    
    const now = new Date();
    const updated = new Date(updatedAt);
    const diffMinutes = Math.floor((now - updated) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Agora mesmo';
    if (diffMinutes < 60) return `há ${diffMinutes} min`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `há ${diffHours}h`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `há ${diffDays} dias`;
  };

  return (
    <>
      <Card
        sx={{
          minWidth: 350,
          maxWidth: 400,
          border: 1,
          borderColor: currentStatus.color === 'default' 
            ? theme.palette.divider 
            : `${currentStatus.bgColor}`,
          borderRadius: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent sx={{ pb: 1 }}>
          {/* Header com ícone e nome */}
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <WhatsApp 
              sx={{ 
                color: theme.palette.success.main,
                fontSize: '2rem' 
              }} 
            />
            <Box flex={1}>
              <Typography variant="h6" component="div" fontWeight={600}>
                {connection.name}
              </Typography>
              {connection.isDefault && (
                <Chip
                  label="Padrão"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
            {currentStatus.icon}
          </Box>

          {/* Status */}
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="body2" color="text.secondary">
              Status:
            </Typography>
            <Chip
              label={currentStatus.label}
              color={currentStatus.color}
              size="small"
              sx={{
                fontWeight: 500,
              }}
            />
          </Box>

          {/* Telefone conectado */}
          {connection.status === 'open' && connection.phone && (
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="body2" color="text.secondary">
                Telefone:
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {connection.phone}
              </Typography>
            </Box>
          )}

          {/* Status reason para falhas */}
          {connection.status === 'failed' && connection.statusReason && (
            <Box mb={1}>
              <Typography variant="body2" color="error">
                Código: {connection.statusReason}
              </Typography>
            </Box>
          )}

          {/* Última atualização */}
          <Typography variant="caption" color="text.secondary">
            Última atualização: {formatLastUpdate(connection.updatedAt)}
          </Typography>
        </CardContent>

        <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
          <Box display="flex" gap={1} width="100%" flexWrap="wrap">
            {connection.status === 'close' || connection.status === 'failed' || connection.status === 'connecting' ? (
              <Button
                variant="contained"
                color="success"
                startIcon={loadingAction === 'connect' ? 
                  <CircularProgress size={16} color="inherit" /> : 
                  <QrCode />
                }
                onClick={handleConnect}
                disabled={loading}
                fullWidth
              >
                {loadingAction === 'connect' ? 'Conectando...' : 
                 connection.status === 'connecting' ? 'Solicitar QR Code' : 'Conectar WhatsApp'}
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={loadingAction === 'disconnect' ? 
                    <CircularProgress size={16} color="inherit" /> : 
                    <PowerOff />
                  }
                  onClick={() => setShowDisconnectDialog(true)}
                  disabled={loading}
                  size="small"
                >
                  {loadingAction === 'disconnect' ? 'Desconectando...' : 'Desconectar'}
                </Button>
                
                <Tooltip title="Reiniciar conexão">
                  <IconButton
                    color="primary"
                    onClick={handleRestart}
                    disabled={loading}
                    size="small"
                  >
                    {loadingAction === 'restart' ? 
                      <CircularProgress size={20} color="inherit" /> : 
                      <Refresh />
                    }
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </CardActions>
      </Card>

      {/* Modal de confirmação para desconectar */}
      <CustomDialog
        open={showDisconnectDialog}
        handleClickClose={() => setShowDisconnectDialog(false)}
        title="Confirmar Desconexão"
        contentText={`Tem certeza que deseja desconectar a instância "${connection.name}"? Esta ação irá parar todas as funcionalidades WhatsApp relacionadas a esta conexão.`}
        handleConfirmationClick={handleDisconnect}
        confirmationClickText="Desconectar"
        requestLoading={loadingAction === 'disconnect'}
        requestLoadingText="Desconectando..."
        width={500}
        height="auto"
      />
    </>
  );
};

export default WhatsAppConnectionCard;
